import { useState } from 'react'
import { formatarCpf, normalizarCpf, validarCpf } from '@precad/compartilhado/validacao/cpf'
import { validarNascimentoBr } from '@precad/compartilhado/validacao/data'
import { normalizarEmail, validarEmail } from '@precad/compartilhado/validacao/email'
import { Campo } from '../componentes/Campo'
import { CampoData } from '../componentes/CampoData'
import type { Socio } from '../tipos'

/**
 * Etapa 4 — quadro societário.
 *
 * Primeira tela que coleta dado pessoal de TERCEIRO. É por isso que o gate do
 * código fica logo antes dela, e por isso o texto de apoio diz para quê os
 * dados servem: pedir CPF sem explicar é o que faz as pessoas abandonarem, e
 * a LGPD pede a informação de qualquer forma.
 */

interface Props {
  socios: Socio[]
  aoMudar: (socios: Socio[]) => void
  aoVoltar: () => void
  aoAvancar: () => void
  ocupado: boolean
}

const VAZIO: Socio = { nome: '', cpf: '', email: '', dataNascimento: '' }

export function Socios({ socios, aoMudar, aoVoltar, aoAvancar, ocupado }: Props) {
  const [erros, setErros] = useState<Record<string, string>>({})

  const lista = socios.length ? socios : [{ ...VAZIO }]

  function alterar(indice: number, campo: keyof Socio, valor: string) {
    const nova = lista.map((s, i) => (i === indice ? { ...s, [campo]: valor } : s))
    aoMudar(nova)
    const chave = `${indice}.${campo}`
    if (erros[chave]) setErros((e) => ({ ...e, [chave]: '' }))
  }

  function acrescentar() {
    aoMudar([...lista, { ...VAZIO }])
  }

  function remover(indice: number) {
    aoMudar(lista.filter((_, i) => i !== indice))
    setErros({})
  }

  function validar(): boolean {
    const novos: Record<string, string> = {}
    const cpfsVistos = new Set<string>()

    lista.forEach((s, i) => {
      if (!s.nome?.trim()) novos[`${i}.nome`] = 'Informe o nome completo.'

      const cpf = normalizarCpf(s.cpf ?? '')
      if (!cpf) novos[`${i}.cpf`] = 'Informe o CPF.'
      else if (!validarCpf(cpf)) novos[`${i}.cpf`] = 'CPF inválido. Confira os números.'
      else if (cpfsVistos.has(cpf)) novos[`${i}.cpf`] = 'Este CPF já foi informado em outro sócio.'
      else cpfsVistos.add(cpf)

      /* E-mail de sócio é opcional, mas errado é pior que ausente — o servidor
         aceitaria e ninguém perceberia. */
      const email = normalizarEmail(s.email ?? '')
      if (email && !validarEmail(email)) novos[`${i}.email`] = 'E-mail inválido, ou deixe em branco.'

      /* Mesma lógica para a data: opcional, mas se preenchida tem de ser real.
         Sem isto, `31/02/1980` seria convertida para vazio na fronteira e o
         campo chegaria NULO ao banco — a pessoa preencheu e nada foi gravado,
         sem nenhum aviso. */
      const nascimento = (s.dataNascimento ?? '').trim()
      if (nascimento && !validarNascimentoBr(nascimento)) {
        novos[`${i}.dataNascimento`] = 'Data inválida. Use DD/MM/AAAA.'
      }
    })

    setErros(novos)
    if (Object.keys(novos).length) {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return false
    }
    return true
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        if (validar()) aoAvancar()
      }}
    >
      <p className="chamada">
        Informe os sócios que constam no contrato social. Estes dados são usados na análise de
        crédito e ficam acessíveis apenas às áreas responsáveis.
      </p>

      {lista.map((socio, i) => (
        <fieldset className="bloco" key={i}>
          <legend className="bloco__titulo">
            Sócio {i + 1}
            {lista.length > 1 ? (
              <button type="button" className="bloco__remover" onClick={() => remover(i)}>
                Remover
              </button>
            ) : null}
          </legend>

          <div className="grade grade--duas">
            <Campo
              rotulo="Nome completo"
              valor={socio.nome ?? ''}
              aoMudar={(v) => alterar(i, 'nome', v)}
              erro={erros[`${i}.nome`]}
              largo
              maxLength={255}
              autoComplete="off"
            />
            <Campo
              rotulo="CPF"
              valor={socio.cpf ?? ''}
              aoMudar={(v) => alterar(i, 'cpf', v)}
              aoSair={() => {
                const cpf = normalizarCpf(socio.cpf ?? '')
                if (validarCpf(cpf)) alterar(i, 'cpf', formatarCpf(cpf))
              }}
              erro={erros[`${i}.cpf`]}
              modo="numeric"
              maxLength={14}
              placeholder="000.000.000-00"
            />
            {/* Máscara para digitar e calendário para escolher. O estado guarda
                DD/MM/AAAA; a conversão para ISO acontece na fronteira, em
                useRascunho. */}
            <CampoData
              rotulo="Data de nascimento"
              valor={socio.dataNascimento ?? ''}
              aoMudar={(v) => alterar(i, 'dataNascimento', v)}
              erro={erros[`${i}.dataNascimento`]}
              opcional
              /* Nascimento no futuro é sempre erro de digitação. */
              ateHoje
            />
            <Campo
              rotulo="E-mail do sócio"
              valor={socio.email ?? ''}
              aoMudar={(v) => alterar(i, 'email', v)}
              erro={erros[`${i}.email`]}
              opcional
              largo
              tipo="email"
              modo="email"
              maxLength={150}
            />
          </div>
        </fieldset>
      ))}

      <button type="button" className="botao botao--secundario botao--largo" onClick={acrescentar}>
        + Acrescentar sócio
      </button>

      <div className="rodape">
        <button type="button" className="botao botao--secundario" onClick={aoVoltar}>
          Voltar
        </button>
        <span className="rodape__espaco" />
        <button type="submit" className="botao botao--principal" aria-disabled={ocupado}>
          {ocupado ? 'Salvando…' : 'Continuar'}
        </button>
      </div>
    </form>
  )
}
